/**
 * AnonymousOrGuarded — a vitrine pública do perfil, aberta a quem não tem
 * conta (F1.5 do Roadmap PRO, 11/09 — `docs/product/roadmap-pro.md`).
 *
 * Decisão do fundador: o visitante sem conta vê **exatamente o mesmo
 * perfil** que um visitante logado veria — não uma versão reduzida (hero +
 * leitura atual). A única diferença é que ele não interage (seguir e tal).
 *
 * ── Por que sessão anônima, e não abrir as Firestore Rules ──────────────────
 *
 * `users`, `user_stats` (+ `daily_pages`) e `user_books` são
 * `allow read: if isAuthed()` — não são owner-only, mas exigem QUALQUER
 * sessão Firebase. Abrir para `if true` exporia toda a base (estatísticas,
 * estante, seguidores) a scraper e bot sem conta nenhuma. Uma sessão anônima
 * (`signInAsGuest`) satisfaz `isAuthed()` sem tocar em uma linha de regra —
 * o visitante lê como leria um usuário real, e nada na Rules muda.
 *
 * ⚠️ Isto NÃO é o mesmo padrão de `PublicOrGuarded` (ficha de livro): aquele
 * não faz NENHUMA leitura no Firestore para o visitante — os dados vêm
 * prontos de um payload injetado no HTML (`renderBookPage`), porque as Rules
 * do catálogo continuam fechadas para anônimo. Aqui a leitura é real, via
 * sessão anônima; não há payload nem function nova.
 *
 * ── O que a sessão anônima NÃO pode virar ────────────────────────────────────
 *
 * Uma sessão anônima é `user.isAnonymous === true`, e TODO outro guard do
 * app (`ProtectedRoute`, `RootEntry`, `PublicOrGuarded`, `AppFrameRoute`)
 * trata isso como "sem sessão" — só este guard admite. Sem essa disciplina,
 * a sessão nascida aqui alcançaria Home, Settings, `/pro` (checkout do
 * Stripe) e `/admin` como se fosse conta de verdade. `usePublicProfileState`
 * também nunca deve tratar `user.isAnonymous` como visitante que pode seguir
 * — ver `viewerIsAnonymous` lá.
 */

import { useEffect, useRef, useState, type ReactElement } from "react";
import { useAuth } from "../hooks/useAuth";
import { signInAsGuest } from "../services/firebase/authService";
import { ProtectedRoute } from "./ProtectedRoute";
import { OnboardingGuard } from "./OnboardingGuard";
import { BetaAccessGate } from "./BetaAccessGate";
import { PageLoader } from "./PageLoader";

export function AnonymousOrGuarded({ children }: { children: ReactElement }) {
  const { user, isAnonymous, isLoading, redirectPending } = useAuth();
  const [guestFailed, setGuestFailed] = useState(false);
  // Trava síncrona, não estado: o StrictMode do React invoca o efeito duas
  // vezes em dev, e um `ref` setado ANTES do `await` bloqueia a 2ª chamada
  // mesmo que a 1ª ainda esteja em voo — um booleano em estado reagiria tarde
  // demais (só no próximo render).
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (isLoading || redirectPending) return;
    if (user) return; // já há sessão (real ou anônima) — nada a fazer
    if (attemptedRef.current) return;
    attemptedRef.current = true;
    void signInAsGuest().catch(() => setGuestFailed(true));
  }, [isLoading, redirectPending, user]);

  if (isLoading || redirectPending) return <PageLoader />;

  // Conta de verdade: mesma cadeia de `Guarded` (regra de ouro da rota) —
  // quem já está logado não muda de comportamento ao visitar um perfil.
  if (user && !isAnonymous) {
    return (
      <ProtectedRoute>
        <BetaAccessGate>
          <OnboardingGuard>{children}</OnboardingGuard>
        </BetaAccessGate>
      </ProtectedRoute>
    );
  }

  if (user && isAnonymous) return children;

  // A sessão anônima falhou (ex.: Anonymous Sign-in desligado no Console).
  // Sem sessão nenhuma, os reads do perfil vão bater em `permission-denied`
  // — melhor mandar para o login do que renderizar uma tela quebrada.
  if (guestFailed) return <ProtectedRoute>{children}</ProtectedRoute>;

  return <PageLoader />;
}
